/**
 * Image utility functions for optimizing photo URLs using Spark CDN resizing
 */

/**
 * Resize Spark Platform CDN image URL
 * @param {string} originalUrl - The original MediaURL from the API
 * @param {number} width - Desired width in pixels
 * @param {number} height - Desired height in pixels
 * @param {boolean} shouldCrop - Whether to crop to exact dimensions (default: true)
 * @returns {string} - Resized image URL
 */
export function resizeSparkImage(originalUrl, width, height, shouldCrop = true) {
  // Validate inputs
  if (!originalUrl || typeof originalUrl !== 'string') {
    console.error('Invalid URL provided');
    return originalUrl;
  }
  
  if (!width || !height || width <= 0 || height <= 0) {
    console.error('Invalid dimensions provided');
    return originalUrl;
  }

  // Regular expression to match both possible domains
  const pattern = /(?:cdn\.photos\.sparkplatform\.com|photos\.sparkplatform\.com)\/(\w+)\/([\w\d\-]+-o\.jpg)/i;
  const match = originalUrl.match(pattern);
  
  // If URL doesn't match Spark pattern, return original
  if (!match) {
    return originalUrl;
  }
  
  // Extract parts
  const [_, site, filename] = match;
  // site = "ore" (or other site code)
  // filename = "20250203183513852280000000-o.jpg"
  
  // Determine crop parameter
  const cropParam = shouldCrop ? 'true' : 'false';
  
  // Construct resized URL
  const resizedUrl = `https://cdn.resize.sparkplatform.com/${site}/${width}x${height}/${cropParam}/${filename}`;
  
  return resizedUrl;
}

/**
 * Generate optimized image URL for gallery view
 * @param {string} originalUrl - The original MediaURL from the API
 * @param {number} width - Desired width in pixels
 * @param {number} height - Desired height in pixels
 * @returns {string} - Optimized image URL
 */
export function getOptimizedImageUrl(originalUrl, width = 400, height = 300) {
  return resizeSparkImage(originalUrl, width, height, true);
}

/**
 * Get gallery-optimized image URL (higher quality for cards)
 * @param {string} originalUrl - The original MediaURL from the API
 * @returns {string} - Gallery-optimized image URL
 */
export function getGalleryImageUrl(originalUrl) {
  // Gallery cards are typically 280px wide with ~65% aspect ratio
  // Doubled resolution for better quality: 1120x720 (2x the previous 560x360)
  return getOptimizedImageUrl(originalUrl, 1120, 720);
}

/**
 * Get modal-optimized image URL (high quality for photo modal)
 * @param {string} originalUrl - The original MediaURL from the API
 * @returns {string} - Modal-optimized image URL
 */
export function getModalImageUrl(originalUrl) {
  // Modal images need high quality - use 2400x1800 for crisp display (modest increase from 1920x1440)
  return getOptimizedImageUrl(originalUrl, 2400, 1800);
}
